import { useState } from "react";

import { CustomLink } from "ui/custom-link";
import { ActionsMenu, MenuItem } from "ui/actions-menu";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import { Route as AppRoute } from "utilities/app-routes";

import { CampaignFolderDTO } from "generated/schemas";

import Icons from "assets";

import { useFolderActions } from "containers/admin/clients/campaign/campaign-creative-page/hooks/use-folder-actions";

interface FolderTableRowProps {
  folder: CampaignFolderDTO | null;
  touchpointCount: number;
  countLabel?: string | number;
  isExpanded: boolean;
  onToggleExpanded?: () => void;
}

const FolderTableRow = ({
  folder,
  touchpointCount,
  countLabel,
  isExpanded,
  onToggleExpanded,
}: FolderTableRowProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();

  const [isFolderDropdownOpen, setIsFolderDropdownOpen] = useState<boolean>(false);

  const handleFolderDropdownOpen = () => setIsFolderDropdownOpen(true);
  const handleFolderDropdownClose = () => setIsFolderDropdownOpen(false);

  const { actions, modalMarkup } = useFolderActions(folder);

  const isExpandable = !!touchpointCount && !!onToggleExpanded;

  return (
    <div className="flex items-center gap-2 justify-between w-full">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={isExpandable ? onToggleExpanded : undefined}
          disabled={!isExpandable}
          aria-label={
            folder
              ? `${isExpanded ? "Collapse" : "Expand"} folder ${folder.name ?? ""}`.trim()
              : `${isExpanded ? "Collapse" : "Expand"} Not in a Folder`.trim()
          }
          className={`p-0 bg-transparent border-0 ${
            isExpandable ? "cursor-pointer" : "cursor-default"
          }`}>
          {isExpandable ? (
            <Icons.ArrowRight
              customClass={`size-5 text-aqua-600 ${isExpanded ? "rotate-90" : ""}`}
            />
          ) : (
            // Invisible icon as a placeholder to reserve space
            <Icons.ArrowRight customClass="size-5 text-aqua-600 opacity-0" />
          )}
        </button>

        {folder ? (
          <>
            <Icons.Folder customClass="size-4 text-gray-400" />
            <CustomLink
              label={folder.name}
              to={`${AppRoute.clients}/${currentClient.id}/${AppRoute.campaigns}/${currentCampaign.id}/${AppRoute.creative}/${AppRoute.folder}/${folder.id}`}
            />
            {!!touchpointCount && (
              <span className="text-gray-600 bg-white border border-gray-200 px-2 text-xs py-0.5 rounded-md">
                {countLabel}
              </span>
            )}
          </>
        ) : (
          <>
            <span className="text-gray-600">Not in a Folder</span>
            {!!touchpointCount && (
              <span className="text-gray-600 bg-white border border-gray-200 px-2 text-xs py-0.5 rounded-md">
                {countLabel}
              </span>
            )}
          </>
        )}
      </div>

      {folder && (
        <>
          <ActionsMenu
            itemName="Folder menu"
            open={isFolderDropdownOpen}
            onOpen={handleFolderDropdownOpen}
            onClose={handleFolderDropdownClose}
            sx={{ justifyContent: "flex-end" }}>
            {actions.map((action) => (
              <MenuItem
                key={action.label}
                icon={action.icon}
                label={action.label}
                onClick={action.onClick}
              />
            ))}
          </ActionsMenu>
          {modalMarkup}
        </>
      )}
    </div>
  );
};

export default FolderTableRow;
